import { View } from "react-native";
import React, { useState } from "react";
import BaseCard, { BaseCardListItem } from "../../../components/BaseCard";
import { FontAwesome } from "@expo/vector-icons";
import Dialog from "components/Dialog";
import { Text } from "@ui-kitten/components";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import toast from "utils/toast";
import toastConfig from "config/toastConfig";
import Toast from "react-native-toast-message";
import { useAccount } from "hooks/useAccount";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ACCOUNT_QUERY_KEYS,
  updateBillingFetcher,
} from "queries/account.queries";
import i18n from "plugins/localization";
import { AccountDetails } from "features/types";
import { getCreditCardIcon, walletTypeLabel } from "utils/billingUtils";

interface Props {
  title?: string;
  variant?: "solid" | "outline";
}
const PaymentMethodCard: React.FC<Props> = (props) => {
  const {
    title = i18n.t("components.PaymentMethodCard.paymentMethod"),
    variant = "solid",
  } = props;

  // local state
  const [dialog, setDialog] = useState(false);
  // react-query
  const { account, updateFetchAccountBffCache } = useAccount();
  const { mutateAsync: updateBilling, isLoading: loading } =
    useMutation(updateBillingFetcher);

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    result: StripeTokenResponse | undefined
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }
    // Update payment method, on success, update the query cache for fetch account bff query
    await updateBilling(
      {
        accountId: account!.id,
        token: result!,
      },
      {
        onSuccess: updateFetchAccountBffCache,
      }
    );
    toast.success(
      i18n.t("components.PaymentMethodCard.paymentMethodUpdatedMessage")
    );
    setDialog(false);
  };

  if (!account) return null;

  const walletLabel = walletTypeLabel(account.billingWalletType);

  return (
    <BaseCard
      title={title}
      style={{
        borderWidth: variant === "solid" ? 0 : 1,
        borderColor: "#c3c3c3",
      }}
    >
      {!!account.billingLast4 && (
        <BaseCardListItem
          showArrow={false}
          title={
            <View>
              <Text style={{ fontWeight: "700" }}>
                {i18n.t("components.PaymentMethodCard.currentCard")}
              </Text>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <FontAwesome
                  name={getCreditCardIcon(account.billingBrand)}
                  size={25}
                />
                <Text>
                  {" "}
                  **** **** **** {account.billingLast4}
                  {walletLabel ? ` (${walletLabel})` : ""}
                </Text>
              </View>
              <Text>
                {i18n.t("components.PaymentMethodCard.expiresOn") as string}{" "}
                {account.billingExpiration}
              </Text>
            </View>
          }
        ></BaseCardListItem>
      )}
      <BaseCardListItem
        title={i18n.t("components.PaymentMethodCard.newCreditCard")}
        onPress={() => setDialog(true)}
        testID="new-credit-card-button"
      ></BaseCardListItem>
      {dialog && (
        <>
          <Dialog
            visible={dialog}
            setVisible={setDialog}
            style={{ padding: 25, width: "95%" }}
            cancelText="CANCEL"
          >
            <Text category="h6" style={{ marginBottom: 15 }}>
              {i18n.t("components.PaymentMethodCard.updatePaymentMethod")}
            </Text>
            <StripePaymentForm
              onTokenReceived={handleTokenReceived}
              loading={loading}
              showAgreementText={false}
              btnText={i18n.t(
                "components.PaymentMethodCard.updatePaymentMethod"
              )}
            />
            <Toast config={toastConfig} />
          </Dialog>
        </>
      )}
    </BaseCard>
  );
};

export default PaymentMethodCard;
